import React from "react";
import { useMeetingColumnVisibilityStore } from "data/store/global-search/MeetingColumnVisibility";
import {
  MeetingTableRow,
  useGlobalSearchMeetingsTable,
} from "data/hooks/useGlobalSearchMeetingsTable";
import { MeetingContent } from "data/@types/GlobalSearchData";
import {
  getSharedEntityColumns,
  GlobalSearchAdvancedTable,
} from "../../../components/global-search/GlobalSearchAdvancedTable";
import { DateFormat, formatDateTimeInUserTimeZone } from "utils/date";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AppIcon from "ui/components/icons/AppIcon";
import { iconCheck, iconClose } from "ui/components/icons";
import { Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useGlobalSearchContext } from "../SearchProvider";

type Props = {
  onBack: () => void;
  meetingRows: MeetingContent[];
  isLoading?: boolean;
};

export function GlobalSearchMeetingsTable({
  onBack,
  meetingRows,
  isLoading,
}: Readonly<Props>) {
  const columnVisibilityModel = useMeetingColumnVisibilityStore(
    (state) => state.meeting,
  );
  const onColumnVisibilityChange = useMeetingColumnVisibilityStore(
    (state) => state.updateColumnVisibility,
  );
  const { meetingFilters, applyFilters, rows, isBackendLoading, fetchError } =
    useGlobalSearchMeetingsTable(meetingRows);
  const { handleClose } = useGlobalSearchContext();

  const meetingColumns: GridColDef[] = [
    { field: "id", headerName: "Meeting ID" },
    {
      headerName: "Entity Legacy ID",
      field: "entityLegacyId",
    },
    {
      headerName: "Entity RCU ID",
      field: "entityId",
    },
    {
      headerName: "Scheduled By ID",
      field: "scheduledById",
    },
    {
      field: "meetingLink",
      headerName: "Meeting Link",
      valueGetter: (_, row) => row?.meetingLink,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Link
            to={`/meeting/${row.id}`}
            component={RouterLink}
            onClick={() => handleClose()}
            sx={{
              textDecoration: "none",
              color: "info.main",
              fontWeight: "bold",
            }}
          >
            {row?.meetingLink}
          </Link>
        );
      },
      minWidth: 330,
      flex: 1,
    },
    ...getSharedEntityColumns(handleClose),
    { field: "authorName", headerName: "Scheduled by" },
    { field: "type", headerName: "Meeting Type", minWidth: 180, flex: 1 },
    {
      field: "startDateTime",
      headerName: "Start Date/Time",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) =>
        formatDateTimeInUserTimeZone(row.startDateTime),
    },
    {
      field: "endDateTime",
      headerName: "End Date/Time",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) => formatDateTimeInUserTimeZone(row.endDateTime),
    },
    {
      field: "isAllDayEvent",
      headerName: "All Day Event",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.isAllDayEvent ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "annualMaterialsReceived",
      headerName: "Annual Materials Received",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.annualMaterialsReceived ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "annualNotesReceived",
      headerName: "Annual Notes Received",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) =>
        formatDateTimeInUserTimeZone(row.annualNotesReceived, {
          customFormat: DateFormat.DATE,
        }),
    },
    {
      field: "inviteReceivedDate",
      headerName: "Invite Received Date",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) =>
        formatDateTimeInUserTimeZone(row.inviteReceivedDate, {
          customFormat: DateFormat.DATE,
        }),
    },
    {
      field: "commentRequired",
      headerName: "No Comment Required",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return !row.commentRequired ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "closed",
      headerName: "Closed/Private",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.closed ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    { field: "topic", headerName: "Topic" },
    { field: "attendeeGroup", headerName: "Department" },
    {
      field: "attendeeTbd",
      headerName: "Attendees TBD (AGM Only)",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.attendeeTbd ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "caNotAttending",
      headerName: "CA Not Attending (AGM Only)",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.caNotAttending ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "caLeaders",
      headerName: "CA Leader(s)",
    },
    {
      field: "caParticipants",
      headerName: "CA Participants",
    },
    {
      field: "managerParticipants",
      headerName: "Manager Participants",
    },
    {
      field: "responsibleWriters",
      headerName: "Responsible Writers",
    },
    {
      headerName: "Other Manager Representatives",
      field: "otherManagers",
    },
    {
      headerName: "Other Meetings Attendees",
      field: "otherMeetingAttendees",
    },
    { field: "style", headerName: "Meeting Style" },
    {
      field: "meetingLocation",
      headerName: "Meeting Location",
    },
    {
      field: "caOffice",
      headerName: "Office Location",
    },
    {
      field: "managerLocation",
      headerName: "Manager Location",
    },

    {
      headerName: "Other Location",
      field: "otherLocation",
    },
    {
      headerName: "Other Country",
      field: "otherCountry",
    },
    {
      field: "createdByPersonName",
      headerName: "Created By",
    },
    {
      field: "creTimest",
      headerName: "Created Date",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) => formatDateTimeInUserTimeZone(row.creTimest),
    },
    {
      field: "updatedByPersonName",
      headerName: "Last Modified By",
    },
    {
      field: "modTimest",
      headerName: "Last Modified Date",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) => formatDateTimeInUserTimeZone(row.modTimest),
    },

    { field: "createdById", headerName: "Created By ID" },
    { field: "updatedById", headerName: "Last Modified By ID" },
  ];

  return (
    <GlobalSearchAdvancedTable<MeetingContent, MeetingTableRow>
      onBack={onBack}
      rows={rows}
      columns={meetingColumns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityChange={onColumnVisibilityChange}
      filters={meetingFilters}
      applyFilters={applyFilters}
      activityType="Meetings"
      getRowId={(row) => `${row.entityType} ${row?.id} ${row.entityId}`}
      emptyTestId="meetings-table-view-empty"
      isLoading={isLoading || isBackendLoading}
      fetchError={fetchError}
    />
  );
}
